/* THE single place to change a project-level default.

   Every value here is needed in two spots that must agree but must not
   import each other: state/store.js's S (what a brand-new session starts
   with) and core/doc.js's migrate() (what a saved file gets for a field it
   predates). Before this existed they were two hand-kept-in-sync literals —
   which is exactly how focal.manualMM drifted (store.js said 24, migrate()
   still said 35) until it was caught by accident.

   A function, not a plain object: several of these are nested objects
   (screen, corners, bg, ...) that calling code goes on to mutate in place
   (S.corners.tl.x = ..., and so on) — handing out the same object reference
   twice would let a mutation in one document's state silently corrupt the
   "default" for the next. Each call returns a fresh set.

   Pure — no DOM, no THREE, no S — so both a leaf module (store.js, which
   deliberately imports nothing else) and a pure one (doc.js, which
   deliberately imports no app state) can depend on it without creating the
   cycle store.js's own header warns about. */
export function projectDefaults() {
  return {
    screen: { w: 3.375, h: 6 },   // 9:16 physical shape, matched to the pixel default below
    res: { w: 1080, h: 1920 },    // vertical by default
    corners: { tl: { x: .22, y: .28 }, tr: { x: .78, y: .24 }, br: { x: .80, y: .70 }, bl: { x: .20, y: .74 } },
    focalMode: 'auto',
    focalManualMM: 24,            // roughly an iPhone's main-camera default
    nudge: { x: 0, y: 0, z: 0 },
    bg: { color: '#101418', transparent: false },
    tone: 'none',
    envInt: 1,
    /* Screen-space ambient occlusion over every object — scene/ao.js. Off by
       default, so a project that never touches it renders exactly as before.
       Reach is a fraction of the screen's shorter side, not metres. */
    aoStrength: 0,
    aoReach: .15,
    /* Depth of field — scene/dof.js. Off by default, like occlusion. The focus
       is the screen surface, a depth in metres (+ toward the viewer), or an
       object that the focus then follows. */
    dofStrength: 0,
    dofFocus: { mode: 'screen', z: 0, id: null },
    shadowRes: 4096,
    shadowSoft: 8,                // blur radius in shadow-map texels — see scene/lighting.js's setShadowSoft
    ss: 2,                        // supersample factor for the flat render
    /* Sub-frames averaged per output frame in the video export — 1 is off, and
       off is the default because this multiplies render time by exactly this
       number and nothing else in the app costs that much. Video only: the
       preview and the PNG have no time axis to blur across. */
    motionBlur: 1,
    gizmoSize: 1,                 // scene/gizmo.js's GIZMO_TARGET is "of stage height, at gizmoSize 1"
    lighting: 'studio',
    /* THE MARGINS between a wall's screens — how they are shown, not how wide
       they are. The width is S.screenArray.gap, which is the installation.
       These three are how you want to look at it.

       In the document, with the rest of the stage settings. They were held out
       of it on the grounds that they cannot reach a deliverable, which is true
       and turns out not to be the question: gizmoSize and shadowRes cannot
       either, and both are saved. What decides it is whether the answer
       belongs to THIS project, and it does — a client mockup wants the
       building showing through the margins, a working file wants them black,
       and neither should have to be set again on every open. */
    showBezels: true,
    bezelOpacity: 1,
    /* On-site only, where the render is warped over the photograph and a
       transparent pixel therefore IS the photograph — see setBezelPass in
       scene/helpers.js. Off by default: black is what an unlit bezel looks
       like, and that is the honest ground for judging artwork. */
    bezelPhoto: false,
    /* The On-site render's choices (Export dialog): resolve to the flat file, when it starts and
       whether it comes back, the room around it, and whether a loaded filmed video is the backdrop.
       null for resolveAt is "the midpoint", for stopsAt "stays flat to the end". They are the project's:
       a client's mockup wants the same margin every time it is rendered. */
    onsite: { resolveToFlat: false, resolveAt: null, stopsAt: null, flatMargin: 0, flatGap: 0, useFilmed: true },
    /* The animation timeline. Ten seconds: signage loops are short, but a
       blank timeline should be a length someone can fill rather than one they
       keep having to extend, and several things here need the room — a move
       from the library carries its own length and grows the duration to fit,
       and the on-site resolve out to flat and back needs 6.4s before it will
       offer the return leg at all. 30fps rather than 25 or 60: it is what
       every player and every screen accepts without a conversation, and it
       halves the render time of 60 for a difference nobody watching a shop
       window will see. */
    /* `loop` is what the PREVIEW does at the end, not a property of the
       deliverable — an exported MP4 is a file, and whether a player repeats
       it is the player's business. On by default because every screen this
       app targets plays its content on a loop, so looping is the honest
       preview; off is for watching a one-shot land without it snapping back
       to the start before you can judge it. */
    timeline: { duration: 10, fps: 30, loop: true },
  };
}
